import { assetUrl } from "@/lib/api";
import { CategoryIcon } from "@/components/z6/CategoryIcon";
import { IconKey } from "@/components/z6/IconKey";
import { Hairline } from "@/components/avant/Avant";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { animate, motion, useMotionValue, useTransform } from "framer-motion";
import { COURSES } from "@/lib/constants";
import { hexToRgba, themeFor } from "@/lib/menuTheme";
import { DishGrid, DrinkCard } from "@/components/z6/DishCard";
import { CourseBar, SoftBackground } from "@/components/z6/Pattern";
import { setSwiping } from "@/lib/perf";
import { ScienceReveal } from "@/components/z6/Science";

const SPRING = { type: "spring", stiffness: 280, damping: 32, mass: 0.9 };

const LiquidTabs = ({ menus, prog, onTap, active }) => {
  const wrap = useRef(null);
  const [rects, setRects] = useState([]);
  useLayoutEffect(() => {
    const measure = () => setRects([...wrap.current.querySelectorAll("[role=tab]")].map((el) => [el.offsetLeft, el.offsetWidth]));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(wrap.current);
    return () => ro.disconnect();
  }, [menus]);
  const lerp = (p, k) => {
    if (!rects.length) return 0;
    const i = Math.max(0, Math.min(rects.length - 1, Math.floor(p))), j = Math.min(rects.length - 1, i + 1), f = Math.max(0, Math.min(1, p - i));
    return rects[i][k] + (rects[j][k] - rects[i][k]) * f;
  };
  const x = useTransform(prog, (p) => lerp(p, 0));
  const w = useTransform(prog, (p) => lerp(p, 1) + Math.sin(Math.PI * (p - Math.floor(p))) * 34);
  const glow = useTransform(prog, (p) => themeFor(menus[Math.round(Math.max(0, Math.min(menus.length - 1, p)))]?.id).glow);
  useEffect(() => { wrap.current?.querySelector(`[aria-selected=true]`)?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" }); }, [active]);
  return (
    <div ref={wrap} role="tablist" data-testid="menu-tabs" className="tabs-mask no-scrollbar relative flex touch-pan-x gap-1 overflow-x-auto overscroll-x-contain px-1">
      <motion.span data-testid="liquid-tab-indicator" aria-hidden className="absolute bottom-1.5 top-1.5 left-0 rounded-full" style={{ x, width: w, background: useTransform(glow, (g) => hexToRgba(g, 0.16)), boxShadow: useTransform(glow, (g) => `inset 0 0 0 1px ${hexToRgba(g, 0.45)}, 0 0 24px ${hexToRgba(g, 0.3)}`) }} />
      {menus.map((m, i) => (
        <button key={m.id} role="tab" aria-selected={m.id === active} data-testid={`menu-tab-${m.id.replace(/_/g, "-")}`} onClick={() => onTap(i)}
          className={`font-ui relative min-h-[50px] shrink-0 whitespace-nowrap px-4 text-[15px] tracking-wide transition-colors duration-300 ${m.id === active ? "text-[#0B2E44] after:absolute after:inset-x-5 after:bottom-2 after:h-[2px] after:rounded-full after:bg-[#F08DB5]" : "text-[#0B2E44] hover:text-[#0B2E44]"}`}>
          {m.short_name}
        </button>
      ))}
    </div>
  );
};

const heroPhoto = (m) => m.hero_circle || (m.card_photo?.fit === "plate" ? m.card_photo.url : null);

const MenuHero = ({ menu, shared }) => {
  const t = themeFor(menu.id);
  const [more, setMore] = useState(false);
  return (
    <motion.section layoutId={shared ? `menu-shell-${menu.id}` : undefined} className="relative overflow-hidden rounded-[30px]"
      style={{ boxShadow: `inset 0 0 0 1px ${hexToRgba(t.glow, 0.25)}, 0 40px 90px -40px ${hexToRgba(t.glow, 0.5)}` }}>
      <SoftBackground testId={`category-hero-bg-${menu.id}`} />
      <div className="relative flex flex-col-reverse gap-4 px-6 py-5 sm:flex-row sm:items-center sm:gap-8 sm:px-9 sm:py-6">
      <div className="min-w-0 max-w-3xl flex-1">
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center self-center rounded-xl bg-white p-1.5 ring-1 ring-[#0B2E44]/10"><CategoryIcon k={menu.id} tone="light" testId={shared ? "menu-title-icon" : undefined} /></span>
          <h1 data-testid="menu-title" className="font-display text-[clamp(32px,6.4vw,48px)] leading-none text-[#0B2E44]">{menu.name}</h1>
          {menu.tagline && <p className="font-accent text-lg text-[#0B2E44]">{menu.tagline}</p>}
        </div>
        <p data-testid="menu-intro" className={`font-ui mt-2 text-[15px] leading-relaxed text-[#0B2E44] ${more ? "" : "line-clamp-2"}`}>{menu.intro}</p>
        <div className="mt-1 flex flex-wrap items-start gap-x-3">
          <button type="button" data-testid={shared ? "menu-intro-more" : undefined} onClick={() => setMore((m) => !m)} aria-expanded={more}
            className="font-ui min-h-[48px] text-[16px] font-normal text-[#0B2E44] underline-offset-4 hover:underline">{more ? "Show less" : "Read more"}</button>
          <div className="min-w-0 flex-1"><ScienceReveal kind="menu" targetId={menu.id} testId={`menu-science-${menu.id}`} /></div>
        </div>
      </div>
      {heroPhoto(menu) && (
        <span className="block aspect-square w-[clamp(120px,22vw,220px)] shrink-0 overflow-hidden rounded-full bg-white ring-1 ring-[#0B2E44]/10">
          <img src={assetUrl(heroPhoto(menu))} alt={menu.name} width={800} height={800} loading="eager" decoding="async" draggable={false}
            data-testid={shared ? "menu-hero-image" : undefined} className="h-full w-full object-cover object-center" />
        </span>
      )}
      </div>
    </motion.section>
  );
};

const Drinks = ({ menu, drinks, onOpen, shared }) => {
  const list = (drinks || []).filter((d) => d.drink_menus?.includes(menu.id));
  if (!list.length) return null;
  return (
    <section className="space-y-6" data-testid={shared ? "menu-drinks-section" : undefined}>
      <div className="flex items-baseline gap-4">
        <h2 className="font-title text-[clamp(26px,5vw,36px)] text-[#0B2E44]">Drinks for this menu</h2>
        <CourseBar course="beverage" />
        <span className="font-mono text-[15px] text-[#0B2E44]">{String(list.length).padStart(2, "0")}</span>
      </div>
      <div data-testid="menu-drinks-row" className="no-scrollbar -mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-2 sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0">{list.map((d) => <div key={d.id} className="w-[80vw] shrink-0 snap-start sm:w-auto"><DrinkCard dish={d} tagged={!!shared} onOpen={shared ? onOpen : () => {}} /></div>)}</div>
    </section>
  );
};

const Pane = ({ menu, dishes, drinks, onOpen, shared }) => (
  <div className="space-y-10">
    <MenuHero menu={menu} shared={shared} />
    {COURSES.map((c) => {
      const list = dishes.filter((d) => d.menus.includes(menu.id) && d.course === c.key);
      return list.length ? (
        <section key={c.key} className="space-y-6" data-testid={shared ? `course-section-${c.key}` : undefined}>
          <div className="flex items-baseline gap-4">
            <span className="font-ui text-[15px] font-normal tracking-[0.3em] text-[#B8326C]">{String(COURSES.indexOf(c) + 1).padStart(2, "0")}</span>
            <h2 className="font-display text-[clamp(30px,6vw,40px)] leading-none text-[#0B2E44]">{c.label}</h2>
            <Hairline className="flex-1 self-center" />
            <span className="font-ui text-[15px] uppercase tracking-[0.28em] text-[#33566B]">{list.length} {list.length === 1 ? "dish" : "dishes"}</span>
          </div>
          {shared ? <DishGrid dishes={list} onOpen={onOpen} active={menu.id} testId={`course-grid-${c.key}`} /> : <div className="grid grid-cols-1 gap-5 opacity-60 sm:grid-cols-2 lg:grid-cols-3" />}
        </section>
      ) : null;
    })}
    <Drinks menu={menu} drinks={drinks} onOpen={onOpen} shared={shared} />
    {shared && <IconKey testId={`menu-key-${menu.id}`} />}
  </div>
);

export const MenuPager = ({ menus, active, onChange, dishes, drinks, onOpen }) => {
  const idx = Math.max(0, menus.findIndex((m) => m.id === active));
  const box = useRef(null);
  const x = useMotionValue(0);
  const prog = useMotionValue(idx);
  useLayoutEffect(() => { x.set(0); prog.set(idx); }, [idx, x, prog]);
  const width = () => box.current?.offsetWidth || window.innerWidth;
  const go = (to, velocity = 0) => {
    const w = width(), dir = to > idx ? -1 : 1;
    animate(prog, to, SPRING);
    animate(x, dir * w, { ...SPRING, velocity }).then(() => { setSwiping(false); onChange(menus[to].id); });
  };
  const onDrag = () => prog.set(idx - x.get() / width());
  const onDragEnd = (_, info) => {
    const w = width(), off = info.offset.x, v = info.velocity.x;
    if ((off < -w * 0.18 || v < -450) && idx < menus.length - 1) go(idx + 1, v);
    else if ((off > w * 0.18 || v > 450) && idx > 0) go(idx - 1, v);
    else { animate(x, 0, SPRING).then(() => setSwiping(false)); animate(prog, idx, SPRING); }
  };
  const tap = (i) => {
    if (i === idx) return;
    if (Math.abs(i - idx) === 1) return go(i);
    animate(prog, i, SPRING);
    onChange(menus[i].id);
  };
  const prev = menus[idx - 1], next = menus[idx + 1];
  return (
    <div data-testid="menu-pager">
      <div className="sticky top-[64px] z-30 -mx-5 px-5 py-2 sm:-mx-8 sm:px-8">
        <div className="glass-bar rounded-full px-1"><LiquidTabs menus={menus} prog={prog} onTap={tap} active={active} /></div>
      </div>
      <div ref={box} className="relative mt-6 overflow-x-clip">
        <motion.div drag="x" dragDirectionLock dragMomentum={false} dragElastic={0.14}
          dragConstraints={{ left: next ? -width() : 0, right: prev ? width() : 0 }}
          onDragStart={() => setSwiping(true)} onDrag={onDrag} onDragEnd={onDragEnd} style={{ x, touchAction: "pan-y", willChange: "transform" }} className="relative">
          {prev && <div className="pointer-events-none absolute top-0 w-full" style={{ left: "-100%" }}><Pane menu={prev} dishes={dishes} drinks={drinks} /></div>}
          <motion.div key={active} initial={{ opacity: 0.4 }} animate={{ opacity: 1 }} transition={{ duration: 0.35 }}><Pane menu={menus[idx]} dishes={dishes} drinks={drinks} onOpen={onOpen} shared /></motion.div>
          {next && <div className="pointer-events-none absolute top-0 w-full" style={{ left: "100%" }}><Pane menu={next} dishes={dishes} drinks={drinks} /></div>}
        </motion.div>
      </div>
    </div>
  );
};
